import useUrlState from '@ahooksjs/use-url-state'
import {
  EyeInvisibleOutlined,
  EyeOutlined,
  FullscreenExitOutlined,
  FullscreenOutlined,
  LeftOutlined,
  RightOutlined,
  SettingOutlined,
} from '@ant-design/icons'
import { useRequest } from 'ahooks'
import {
  Alert,
  Anchor,
  BackTop,
  Button,
  Cascader,
  DatePicker,
  Empty,
  Modal,
  Spin,
  Timeline,
  Tooltip,
  Typography,
} from 'antd'
import axios from 'axios'
// @ts-ignore
import * as d3hierarchy from 'd3-hierarchy'
import { useAtom } from 'jotai'
import { flatMap, sortBy } from 'lodash'
import moment from 'moment'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { DepartmentName, getPointPermission } from '@/components'
import { okrCyclesAtom } from '@/modules/okr'
import { ReportAutoGenerate, ReportRisk, calReportUpdateTime } from '@/modules/project/project-detail/project-report'
import {
  PAGE_RESOURCE_POINTS_ENUM,
  Report,
  ReportConfiguration,
  ReportConfigurationModule,
  ReportExtra,
  ReportSnapshot,
  ResourcePermission,
  ResourceType,
} from '@/schemas'

import { ReportConfigurationContainer } from './report-configuration.component'
import { ReportExtras } from './report-extra.component'
import { ReportGroup } from './report-group.component'
import { Node } from './report-snapshots.component'
import { SnapshotsRelationCreator } from './snapshots-relation-creator.component'

interface PortfolioReportContainerProps extends ResourcePermission {
  selfId: string
  name: string
  type: number
  status?: number
}

export const ReportContainer: React.FC<PortfolioReportContainerProps> = ({
  selfId,
  type,
  name,
  status,
  permissions,
}) => {
  const { t } = useTranslation()
  const [okrCycles] = useAtom(okrCyclesAtom)
  const [state, setState] = useUrlState(
    () => ({
      startDate: (moment().isoWeekday() === 1 ? moment().subtract(1, 'day') : moment()).format('ll'),
    }),
    {
      navigateMode: 'replace',
    }
  )
  const [isFull, setIsFull] = useState(false)
  const [isShowAnchor, setIsShowAnchor] = useState(true)
  const [isShowSetting, setIsShowSetting] = useState(false)
  const [showModuleKR, setShowModuleKR] = useState<any[]>([])
  const [expandSnapshotIds, setExpandSnapshotIds] = useState<string[]>([])

  const { editable: editableContent } = getPointPermission(PAGE_RESOURCE_POINTS_ENUM.周报_内容, permissions)
  const {
    canCreate: canCreateSetting,
    canDelete: canDeleteSetting,
    canUpdate: canUpdateSetting,
    canRead: canReadSetting,
  } = getPointPermission(PAGE_RESOURCE_POINTS_ENUM.周报_配置, permissions)

  const { data, refresh, loading } = useRequest<Report[]>(
    {
      url: '/api/report/getReports',
      params: {
        selfId,
        type,
        ...(type !== ResourceType.PROJECT && { startDate: moment(state.startDate).isoWeekday(1).format('ll') }),
      },
    },
    {
      refreshOnWindowFocus: false,
      refreshDeps: [selfId, state.startDate, status],
    }
  )

  const { data: config, refresh: refreshConfig } = useRequest<ReportConfiguration>(
    {
      url: '/api/report/getReportConfig',
      params: {
        selfId,
        type,
        startDate: moment(state.startDate).isoWeekday(1).format('ll'),
      },
    },
    {
      refreshOnWindowFocus: false,
      refreshDeps: [selfId, state.startDate],
    }
  )

  const { run: upsertModule } = useRequest(
    (data: ReportConfigurationModule) => ({
      url: '/api/report/upsertReportConfigModule',
      method: 'post',
      data: {
        selfId,
        type,
        ...data,
      },
    }),
    {
      manual: true,
      throwOnError: true,
      onSuccess: () => refresh(),
    }
  )

  React.useEffect(() => {
    if (config) {
      let { modules } = config
      modules = modules.filter((m) => m.isChecked && !m.isDeleted)
      setShowModuleKR(
        modules.map((m) => ({
          id: m.id,
          isShowKR: false,
        }))
      )
    }
  }, [config])

  if (!okrCycles.length) return null

  const getLastWeekReport = async (): Promise<ReportExtra[] | undefined> => {
    const res = await axios.request({
      url: '/api/report/getReports',
      params: {
        selfId,
        type,
        startDate: moment(state.startDate).isoWeekday(1).subtract(1, 'week').format('ll'),
      },
    })
    if (res.data && res.data.length > 0) {
      return res.data[0].extras
    }
    return undefined
  }

  const changeScreen = () => {
    setIsFull(!isFull)
    const dom = document.querySelector('.fullscreen-container')
    if (isFull) {
      exitFull()
    } else {
      toFull(dom)
    }
  }

  const onFullscreenchange = () => {
    if (document.fullscreenElement || document['webkitFullscreenElement'] || document['mozFullScreenElement']) {
      setIsFull(true)
    } else {
      setIsFull(false)
    }
  }

  const exitFull = () => {
    if ((document as any).exitFullScreen) {
      ;(document as any).exitFullScreen()
    } else if ((document as any).mozCancelFullScreen) {
      ;(document as any).mozCancelFullScreen()
    } else if ((document as any).webkitExitFullscreen) {
      ;(document as any).webkitExitFullscreen()
    } else if ((document as any).msExitFullscreen) {
      ;(document as any).msExitFullscreen()
    }
  }

  const toFull = (element: any) => {
    if (element.requestFullscreen) {
      return element.requestFullscreen()
    } else if (element.webkitRequestFullScreen) {
      return element.webkitRequestFullScreen()
    } else if (element.mozRequestFullScreen) {
      return element.mozRequestFullScreen()
    } else {
      return element.msRequestFullscreen()
    }
  }

  document.addEventListener('fullscreenchange', onFullscreenchange)
  document.addEventListener('webkitfullscreenchange', onFullscreenchange)
  document.addEventListener('mozfullscreenchange', onFullscreenchange)

  const onScroll = () => {
    const fixedContent = document.querySelector('.report-extra-field--focus')
    if (fixedContent) {
      const top = fixedContent.getBoundingClientRect().top
      const bottom = fixedContent.getBoundingClientRect().bottom
      if (top <= 72 && bottom >= 200) {
        fixedContent.setAttribute('class', 'report-extra-field--focus report-extra-field--fixed')
      } else {
        fixedContent.setAttribute('class', 'report-extra-field--focus')
      }
    }
  }

  document.addEventListener('scroll', onScroll)

  const rendAnchor = (data: Node<ReportSnapshot>[]) => (
    <>
      {data.map((s) => (
        <Anchor.Link
          key={s.data.id}
          href={`#${s.data.id}`}
          title={
            isShowAnchor ? (
              <span className='font-12px font-400 text-blue-gray-500'>
                {s.data.type === ResourceType.TEAM ? (
                  <DepartmentName
                    departmentId={s.data.departmentId}
                    canRead={
                      getPointPermission(
                        PAGE_RESOURCE_POINTS_ENUM.周报_内容,
                        s.data.permissions ?? s.data.report.permissions
                      ).canRead
                    }
                  />
                ) : (
                  s.data.name
                )}
              </span>
            ) : (
              <Tooltip title={s.data.name}>
                <div className={`!w-${24 - s.depth * 8}px h-2px bg-light-900 hover:bg-blue-500`}></div>
              </Tooltip>
            )
          }
          className={isShowAnchor ? '' : '!pl-8px'}
        >
          {s.children && rendAnchor(s.children)}
        </Anchor.Link>
      ))}
    </>
  )

  const cascaderOptions = [
    {
      value: 1,
      label: (
        <div className='flex flex-col space-y-5px'>
          <span>{t('保持实时更新')}</span>
          <span className='text-12px text-gray-400'>{t('实时拉取部门下的目标、目标下的项目，建议季度初选择')}</span>
        </div>
      ),
      children: okrCycles.map((c) => ({ value: c.cycleId, label: c.name })),
    },
    {
      value: 0,
      label: (
        <div className='flex flex-col space-y-5px'>
          <span>{t('继承上一周')}</span>
          <span className='text-12px text-gray-400'>{t('和上周的集合内容结构保持一致')}</span>
        </div>
      ),
    },
  ]

  const cascaderChange = (module: ReportConfigurationModule, value: number[]) => {
    if (value[0]) {
      upsertModule({
        ...module,
        isImmediately: true,
        objectiveCycleId: value[1],
      })
    } else {
      upsertModule({
        ...module,
        isImmediately: false,
      })
    }
  }

  let child: React.ReactNode = null
  const reportPage = (report: Report) => {
    if (!data || !config) return null
    const snapshots = report.snapshots
      .map((d) =>
        d.parentId
          ? d
          : {
              ...d,
              parentId: 'root',
            }
      )
      .concat({ id: 'root', parentId: null } as any)
    const editable = editableContent && !isFull
    const modules = sortBy(
      report.type === ResourceType.PROJECT
        ? report['config'].modules.filter((m: ReportConfigurationModule) => !m.isDeleted && m.isChecked)
        : config.modules.filter((m) => !m.isDeleted && m.isChecked),
      ['order', 'createdAt']
    )
    const tree: Node<ReportSnapshot> = d3hierarchy.stratify({
      id: (d: ReportSnapshot) => d.selfId,
      parentId: (d: ReportSnapshot) => d.parentId,
    })(snapshots)
    return (
      <div className='flex mb-32px justify-between' key={report.id}>
        <div className='space-y-32px w-full'>
          {modules.map((m) => {
            const { isExtra } = m
            const isShowKR = showModuleKR.find((setting) => setting.id === m.id)?.isShowKR || false
            const moduleSnapshots = tree.children ? tree.children.filter((child) => child.data.moduleId === m.id) : []
            const disabledSelfIds = flatMap(moduleSnapshots, (s: Node<ReportSnapshot>) => {
              return s.descendants().map((d: Node<ReportSnapshot>) => d.data.selfId)
            })
            const childrenType = flatMap(moduleSnapshots, (s: Node<ReportSnapshot>) => {
              return s.descendants().map((d: Node<ReportSnapshot>) => d.data.type)
            })
            return (
              <div key={m.id}>
                <div className='flex items-center justify-between report-header'>
                  <div className='flex space-x-10px items-center group'>
                    <Typography.Title level={4} className='z-2 !mb-0'>
                      {type !== ResourceType.PROJECT && (
                        <>
                          <span className='italic'>{t(m.name)}</span>
                          {childrenType.includes(ResourceType.OBJECTIVE) && (
                            <span
                              className='cursor-pointer font-normal ml-15px text-14px'
                              onClick={() =>
                                setShowModuleKR(
                                  showModuleKR.map((setting) => {
                                    if (setting.id === m.id) setting.isShowKR = !isShowKR
                                    return setting
                                  })
                                )
                              }
                            >
                              {isShowKR ? (
                                <>
                                  <EyeOutlined className='mr-5px' />
                                  {t('KR 已显示')}
                                </>
                              ) : (
                                <>
                                  <EyeInvisibleOutlined className='mr-5px' />
                                  {t('KR 已隐藏')}
                                </>
                              )}
                            </span>
                          )}
                          {editable && !m.isExtra && (
                            <SnapshotsRelationCreator
                              rootSelfId={selfId}
                              selfId={selfId}
                              snapshot={{ reportId: report.id, moduleId: m.id } as any}
                              depth={0}
                              onSuccess={refresh}
                              disabledSelfIds={disabledSelfIds}
                              fields={m.fields}
                            >
                              <Button
                                size='small'
                                type='link'
                                className='text-xs ml-5px opacity-0 transition !px-0 group-hover:opacity-100'
                                onClick={(e) => e.stopPropagation()}
                              >
                                {t('edit')}
                              </Button>
                            </SnapshotsRelationCreator>
                          )}
                        </>
                      )}
                      {type === ResourceType.PROJECT && (
                        <span>
                          {moment(report.startDate).format('ll')} - {moment(report.endDate).format('ll')}
                        </span>
                      )}
                    </Typography.Title>
                    {editable && isExtra && (
                      <ReportAutoGenerate report={report} onSuccess={refresh} getLastWeekReport={getLastWeekReport} />
                    )}
                    {/* TODO  历史bug */}
                    {/* <Tooltip title={t('copy report')} className='cursor-pointer text-blue-600'>
                      <CopyOutlined
                        onClick={() => {
                          copyReportToClipboard(report)
                          message.success(t('copy success'))
                        }}
                      />
                    </Tooltip> */}
                  </div>
                  {isExtra && <ReportRisk report={report} disabled={!editable} />}
                  {!isExtra && report.status === 1 && (
                    <span>
                      <span style={{ color: '#8793AB' }}>{t('数据来源')}:</span>
                      <Cascader
                        options={cascaderOptions}
                        onChange={(value: any) => cascaderChange(m, value)}
                        className='ml-5px w-200px'
                        disabled={!canUpdateSetting}
                        allowClear={false}
                        defaultValue={
                          m.isImmediately && m.objectiveCycleId
                            ? [Number(m.isImmediately), m.objectiveCycleId]
                            : [Number(m.isImmediately)]
                        }
                        displayRender={(_, s) => {
                          if (s?.[0].value) {
                            return `${t('保持实时更新')}${s[1] ? ` / ${s[1]?.label}` : ''}`
                          }
                          return t('继承上一周')
                        }}
                      />
                    </span>
                  )}
                </div>
                {isExtra && (
                  <ReportExtras report={report} editable={editable} extraFields={m.fields} refresh={refresh} />
                )}
                {!isExtra && (
                  <ReportGroup
                    selfId={selfId}
                    type={type}
                    name={name}
                    domId={m.id}
                    snapshots={moduleSnapshots}
                    editable={editable && !isFull}
                    reportId={report.id}
                    isShowKR={isShowKR}
                    isFull={isFull}
                    refresh={refresh}
                    expandSnapshotIds={expandSnapshotIds}
                  />
                )}
              </div>
            )
          })}
        </div>
        {type !== ResourceType.PROJECT && (
          <div className='flex flex-col flex-shrink-0 ml-20px max-h-2/3 items-end'>
            {isFull && <FullscreenExitOutlined className='text-24px hover:text-blue-400' onClick={changeScreen} />}
            <Anchor
              getContainer={() =>
                isFull ? (document.querySelector('.fullscreen-container') as HTMLElement) || window : window
              }
              onClick={(e, { href }) => {
                e.preventDefault()
                setExpandSnapshotIds(snapshots.find((s) => s.id === href.split('#')[1])?.path.split('.') || [])
              }}
              offsetTop={200}
              targetOffset={70}
              className={`bg-white ${
                isShowAnchor ? 'w-200px' : 'w-60px'
              } h-50vh overflow-y-auto transition-right duration-50 ease-linear`}
            >
              {modules.map((m) => (
                <Anchor.Link
                  key={m.id}
                  href={`#${m.id}`}
                  className='font-600'
                  title={
                    isShowAnchor ? (
                      <span>{t(m.name)}</span>
                    ) : (
                      <Tooltip title={m.name}>
                        <div className='bg-light-900 h-2px w-42px hover:bg-blue-500'></div>
                      </Tooltip>
                    )
                  }
                >
                  {tree.children && rendAnchor(tree.children.filter((child) => child.data.moduleId === m.id))}
                </Anchor.Link>
              ))}
            </Anchor>
            <Button
              type='text'
              className={`report-fixed-middle fixed border-r-0 z-1 h-48px text-blue-400 transition-right duration-50 ease-linear ${
                isShowAnchor ? 'right-225px' : 'right-0'
              }`}
              icon={isShowAnchor ? <RightOutlined /> : <LeftOutlined />}
              onClick={() => setIsShowAnchor(!isShowAnchor)}
            ></Button>
          </div>
        )}
      </div>
    )
  }

  const latestRisk = data && data[0]?.risk
  const latestProblem = data && data[0]?.extras?.find((e) => e.field === '问题风险')?.content
  const hasRisk = latestRisk && ['中', '高', '极高'].includes(latestRisk)
  const alert = hasRisk && !latestProblem && (
    <Alert className='mb-10px inline-flex' showIcon type='warning' message={t('risk.tip', { latestRisk })} />
  )

  const renderProjectStage = () => {
    if (!data || !config) return null
    const report = data.find((s) => moment(s.endDate).format('ll') === moment(state.startDate).format('ll'))

    return (
      <div className='space-y-20px flex-shrink-0 ml-20px w-400px'>
        <>
          <div className='flex justify-between items-center'>
            <div className=' font-bold'>阶段快照</div>
            <div className='space-x-10px'>
              <LeftOutlined
                className='hover:text-blue-400'
                onClick={() =>
                  setState({ ...state, startDate: moment(state.startDate).subtract(1, 'week').format('ll') })
                }
              />
              <DatePicker.WeekPicker
                style={{ width: 220 }}
                allowClear={false}
                format={(v) => `${v.isoWeekday(1).format('l')} - ${v.isoWeekday(7).format('l')}`}
                value={moment(state.startDate)}
                onChange={(startDate) => setState({ ...state, startDate: moment(startDate).format('ll') })}
                disabledDate={(date) => date.isAfter(moment())}
              />
              <RightOutlined
                className='hover:text-blue-400'
                onClick={() => setState({ ...state, startDate: moment(state.startDate).add(1, 'week').format('ll') })}
              />
            </div>
          </div>
          {report && (
            <div className='pt-10px pl-20px'>
              {report.snapshots
                .slice()
                .reverse()
                .map((s) => (
                  <Timeline.Item key={s.id} color={{ 0: 'gray', 1: 'blue', 2: 'green', 3: 'red' }[s.status]}>
                    <div>{s.description || s.name}</div>
                    <Typography.Text type='secondary' className='text-xs'>
                      {s.name}
                      {s.endDate ? ' · ' + moment(s.endDate).format('l') : null}
                    </Typography.Text>
                  </Timeline.Item>
                ))}
            </div>
          )}
        </>
      </div>
    )
  }

  child = (
    <Spin spinning={loading} delay={100}>
      {data && data.length ? (
        <div className='flex'>
          <div className='w-full'>
            {data?.map((report: Report) => {
              return reportPage(report)
            })}
          </div>
          {type === ResourceType.PROJECT && renderProjectStage()}
        </div>
      ) : (
        <Empty />
      )}
    </Spin>
  )

  return (
    <>
      <div className='border-solid flex border-0 border-b-1 border-light-500 mb-20px pb-16px text-20px justify-between'>
        <div className='flex space-x-10px items-center'>
          {type !== ResourceType.PROJECT && (
            <>
              <LeftOutlined
                className='text-gray-400 text-16px hover:text-blue-400'
                onClick={() =>
                  setState({ ...state, startDate: moment(state.startDate).subtract(1, 'week').format('ll') })
                }
              />
              <RightOutlined
                className='text-gray-400 text-16px hover:text-blue-400'
                onClick={() => setState({ ...state, startDate: moment(state.startDate).add(1, 'week').format('ll') })}
              />
              <DatePicker.WeekPicker
                style={{ width: 220 }}
                allowClear={false}
                format={(v) => `${v.isoWeekday(1).format('l')} - ${v.isoWeekday(7).format('l')}`}
                value={moment(state.startDate)}
                onChange={(startDate) => setState({ ...state, startDate: moment(startDate).format('ll') })}
                disabledDate={(date) => date.isAfter(moment())}
              />
              {data && data[0] && (
                <Typography.Text className='text-blue-500 text-14px'>{`${moment(calReportUpdateTime(data[0]))
                  .utc()
                  .format('YYYY.MM.DD HH:mm:ss')} ${t('updated')}`}</Typography.Text>
              )}
            </>
          )}
        </div>
        <div className='flex space-x-10px items-center'>
          {canReadSetting && data && data[0] && data[0].status === 1 && (
            <SettingOutlined
              className='text-gray-400 text-16px hover:text-blue-400'
              onClick={() => setIsShowSetting(true)}
            />
          )}
          {isFull ? (
            <FullscreenExitOutlined className='text-gray-400 text-16px hover:text-blue-400' onClick={changeScreen} />
          ) : (
            <FullscreenOutlined className='text-gray-400 text-16px hover:text-blue-400' onClick={changeScreen} />
          )}
        </div>
      </div>
      {alert}
      <div
        className={
          isFull ? 'fullscreen-container min-h-50vh p-30px bg-white !overflow-auto' : 'fullscreen-container min-h-50vh'
        }
      >
        {child}
      </div>
      <Modal
        title={t('周报配置')}
        width={880}
        visible={isShowSetting}
        footer={false}
        okType={undefined}
        onCancel={() => {
          setIsShowSetting(false)
          refresh()
        }}
        bodyStyle={{ height: '600px', overflowY: 'auto' }}
      >
        {config && (
          <ReportConfigurationContainer
            selfId={selfId}
            type={type}
            canCreate={canCreateSetting}
            canUpdate={canUpdateSetting}
            canDelete={canDeleteSetting}
            config={config}
            refreshConfig={refreshConfig}
          />
        )}
      </Modal>
      <BackTop />
    </>
  )
}
